<script setup lang="ts">
import { computed, provide, ref, watch } from "vue";
import { getRandomBg } from "../utils/randomBg";
import { activeRoomState } from "../states/active_room";
import { usersVideoTracks } from "../states/usersVideoStreams";
import { currentSpeakers } from "../states/currentSpeakers";
import { authState } from "../states/auth";
import ThreeDots from "./icons/ThreeDots.vue";
import CrownIcon from "./icons/CrownIcon.vue";
import {
  getUserById,
  userMap,
  usersState,
  selectedOneUserViewId,
} from "../states/usersState";
import { isAdmin, isSelfAdmin } from "../states/adminsState";
import { isHandUp } from "../states/handUpState";
import UserItemControls from "./UserItemControls.vue";
import { cameraTrack, desiredMediaState } from "../states/media/state";

const props = defineProps<{
  userId: string;
  avatar?: string;
  displayedName: string;
  size: {
    width: number;
    height: number;
  };
}>();
const threeDots = ref<typeof ThreeDots | null>(null);

const opened = ref(false);
provide("opened-controls", opened);
const toOpen = () => {
  if (isSelfAdmin.value && props.userId !== authState.id) {
    if (!opened.value) {
      opened.value = true;
    } else {
      opened.value = false;
    }
  }
};

const isVideo = computed(() => {
  //self
  if (props.userId === authState.id) {
    return desiredMediaState.cameraEnabled && cameraTrack.value;
  } else {
    //other
    const user = usersState.users.find((u) => u.id === props.userId);
    return user?.camera && !!usersVideoTracks[props.userId];
  }
});

const track = computed(() => {
  //self
  if (props.userId === authState.id) {
    return cameraTrack.value;
  } else {
    //other
    return usersVideoTracks[props.userId];
  }
})

const videoEl = ref<HTMLVideoElement | null>(null);
watch(
  () => {
    return [videoEl.value, track.value];
  },
  () => {
    if(videoEl.value && track.value) {
      track.value?.attach(videoEl.value);
    }
  },
);

const tile_info = computed(() => {
  const res = {
    name: "",
    avatar: props.avatar,
  };
  if (props.displayedName) {
    const nf = props.displayedName.split(" ");
    res.name += nf[0][0].toUpperCase();
    if (nf[1] && nf[1].length > 0) {
      res.name += nf[1][0].toUpperCase();
    }
  }
  return res;
});

defineExpose({
  videoEl,
});

const avatarSize = computed(() => {
  return {
    width: props.size.height / 2 + "px",
    height: props.size.height / 2 + "px",
  };
});
const goToOne = () => {
  if (getUserById(props.userId)?.camera) {
    activeRoomState.mode = "one";
    selectedOneUserViewId.value = props.userId;
  }
};
const user = computed(() => {
  return getUserById(props.userId);
});
</script>
<template>
  <div
    @dblclick="goToOne"
    class="custom-video"
    :style="{
      width: `${size.width}px`,
      height: `${size.height}px`,
    }"
    :class="{
      speak: currentSpeakers.includes(userId),
    }"
  >
    <video
      v-if="isVideo"
      class="videoEl"
      :class="{
        reverse: userId === authState.id,
      }"
      ref="videoEl"
      :width="size.width"
      :height="size.height"
      autoplay
      :controls="false"
    />
    <div
      v-else
      class="image-preview"
      :style="{
        width: `${size.width}px`,
        height: `${size.height}px`,
      }"
    >
      <div
        class="preview-avatar"
        :style="{
          backgroundColor:
            tile_info.name && !tile_info.avatar
              ? getRandomBg(tile_info.name)
              : '#A4A4A4',
          ...avatarSize,
        }"
      >
        <img
          v-if="tile_info.avatar"
          class="covered"
          :style="avatarSize"
          :src="tile_info.avatar"
          alt=""
        />
        <span v-else-if="tile_info.name" class="covered name-char">{{
          tile_info.name
        }}</span>
        <img v-else src="@images/default_avatar.svg" class="covered" />
      </div>
    </div>
    <div
      class="info"
      :class="{
        opened,
        rpadding: isSelfAdmin && userId !== authState.id,
      }"
    >
      <div class="content">
        <CrownIcon v-if="isAdmin(userId)" />
        <span class="name">{{ displayedName }}</span>
        <svg
          v-if="isHandUp(userId)"
          class="hand-up-icon"
          width="21"
          height="20"
          viewBox="0 0 21 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.149 6.25532L14.1385 9.2493H13.3994L13.4099 3.73265C13.4103 3.24031 13.0104 2.84047 12.5181 2.84089C12.0253 2.84089 11.6263 3.23989 11.6263 3.73265L11.6158 9.2493H10.8767L10.8872 2.89176C10.8876 2.39942 10.4878 1.99958 9.99544 2C9.50268 2 9.10367 2.399 9.10367 2.89176L9.09316 9.2493H8.35402L8.36453 4.57354C8.36495 4.0812 7.96511 3.68136 7.47277 3.68178C6.98001 3.68178 6.58101 4.08078 6.58101 4.57354L6.57008 12.4325L5.09766 10.9595C4.63306 10.4949 3.88047 10.4949 3.41588 10.9595C2.95129 11.4241 2.95129 12.1767 3.41546 12.6409L7.36556 16.5915C8.27372 17.4997 9.48123 18 10.7661 18C12.1872 18 13.4763 17.4215 14.4097 16.4881C15.3431 15.5547 15.9212 14.266 15.9216 12.8445L15.9321 6.25574C15.9321 5.76297 15.5331 5.36397 15.0403 5.36397C14.5476 5.36397 14.1486 5.76297 14.149 6.25532Z"
            fill="#EFB55F"
          />
        </svg>
        <svg
          v-if="!userMap[userId]?.micro"
          width="20"
          class="micro-icon"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          :style="{}"
        >
          <path
            d="M4.99967 8.5V9.25C4.99967 10.6424 5.52646 11.9777 6.46414 12.9623C7.40182 13.9469 8.67359 14.5 9.99967 14.5M9.99967 14.5C11.3258 14.5 12.5975 13.9469 13.5352 12.9623C13.5702 12.9256 13.6045 12.8884 13.6384 12.8508M9.99967 14.5V17.5M14.9997 8.5V9.25C14.9997 9.74091 14.9342 10.2247 14.8083 10.6886M9.99967 17.5H8.74967M9.99967 17.5H11.2497M3.33301 2.5L11.5747 10.7756M16.6663 15.9166L13.6384 12.8508M7.85682 7.04241V9.25C7.85682 10.4926 8.81621 11.5 9.99967 11.5C10.6225 11.5 11.1832 11.221 11.5747 10.7756M11.5747 10.7756L13.277 12.4849L13.6384 12.8508M12.1425 7.91667V4.75C12.1425 3.50736 11.1831 2.5 9.99967 2.5C9.14116 2.5 8.40056 3.03011 8.05859 3.7956"
            stroke="#A4A4A4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <ThreeDots
          v-if="isSelfAdmin && userId !== authState.id && user"
          ref="threeDots"
          class="threedots"
          @click="toOpen"
        />
        <UserItemControls v-if="user" :user="user" :control="threeDots?.item" />
      </div>
    </div>
  </div>
</template>
<style scoped>
.custom-video {
  overflow: hidden;
  border-radius: 8px;
  height: 100%;
  width: 100%;
  position: relative;
}

.micro-icon {
  flex-shrink: 0;
}

.hand-up-icon {
  flex-shrink: 0;
  height: 20px;
  width: 20px;
}

.custom-video::after {
  position: absolute;
  content: "";
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 4px solid transparent;
  border-radius: 8px;
  z-index: 10;
  transition: border-color ease-in 150ms;
}

.info {
  height: 36px;
  width: 100%;
  position: absolute;
  bottom: 12px;
  display: flex;
  justify-content: center;
  z-index: 15;
  user-select: text;
}

.info .content {
  height: 100%;
  max-width: 80%;
  border-radius: 8px;
  background: rgba(41, 41, 41, 0.85);

  backdrop-filter: blur(2px);
  display: inline-flex;
  padding: 4px 12px 4px 12px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.info.rpadding .content {
  padding: 4px 8px 4px 12px;
}

.info .content .name {
  color: #ececec;

  text-align: center;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

video {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
}

.image-preview {
  display: flex;
  border-radius: 8px;
  background-color: #747474;
  align-items: center;
  justify-content: center;
}

.preview-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
}

.preview-avatar img {
  height: 100%;
}

.name-char {
  font-size: 30px;
  color: #ffffff;
  text-align: center;
}

.videoEl {
  background: #292929;
}

.speak::after {
  border-color: #8dca35;
}

.threedots {
  cursor: pointer;
}

video.reverse {
  transform: scaleX(-1);
}
</style>
